import { useState, useCallback, useEffect } from 'react';

const QUICK_SALE_UI_SETTINGS_KEY = 'quickSaleUISettings';

const DEFAULT_CARD_SIZE = 100;
const DEFAULT_FONT_SIZE = 100;
const DEFAULT_CATEGORY_TAB_SIZE = 100;
const DEFAULT_IMAGE_SIZE = 100;
const DEFAULT_CONTROL_PANEL_POSITION: 'left' | 'right' = 'right';
const DEFAULT_SHOW_PRODUCT_IMAGES = false; // ✅ YENİ: Varsayılan olarak kapalı
const DEFAULT_SHOW_PRODUCT_PRICES = false; // ✅ YENİ: Varsayılan olarak kapalı
const DEFAULT_CART_COLUMN_WIDTH = 5; // ✅ YENİ: Sepet sütunu genişliği (Grid 12'lik sistem - varsayılan 5)
const DEFAULT_SHOW_ALL_CATEGORY = true; // ✅ YENİ: "Tümü" kategorisi varsayılan açık
const DEFAULT_SHOW_SEARCH_BAR = true; // ✅ YENİ: Arama bölümü varsayılan açık
const DEFAULT_CATEGORY_LAYOUT: 'horizontal' | 'vertical' = 'horizontal'; // ✅ YENİ: Kategori butonları yatay/dikey
const DEFAULT_CATEGORY_POSITION: 'left' | 'right' = 'left'; // ✅ YENİ: Dikey modda kategori pozisyonu
const DEFAULT_CATEGORY_FONT_SIZE = 100; // ✅ YENİ: Kategori yazı boyutu

interface UISettings {
  cardSize: number;
  fontSize: number;
  categoryTabSize: number;
  imageSize: number;
  controlPanelPosition: 'left' | 'right';
  showProductImages?: boolean; // ✅ YENİ
  showProductPrices?: boolean; // ✅ YENİ
  cartColumnWidth?: number; // ✅ YENİ: Sepet sütunu genişliği (3-7 arası)
  showAllCategory?: boolean; // ✅ YENİ: "Tümü" kategorisi gösterilsin mi
  showSearchBar?: boolean; // ✅ YENİ: Arama bölümü gösterilsin mi
  categoryLayout?: 'horizontal' | 'vertical'; // ✅ YENİ: Kategori butonları yatay/dikey
  categoryPosition?: 'left' | 'right'; // ✅ YENİ: Dikey modda kategori pozisyonu
  categoryFontSize?: number; // ✅ YENİ: Kategori yazı boyutu
}

interface UseQuickSaleUIReturn {
  // State
  productCardSize: number;
  productCardFontSize: number;
  productCategoryTabSize: number;
  productImageSize: number;
  controlPanelPosition: 'left' | 'right';
  showProductImages: boolean; // ✅ YENİ
  showProductPrices: boolean; // ✅ YENİ
  cartColumnWidth: number; // ✅ YENİ: Sepet sütunu genişliği
  showAllCategory: boolean; // ✅ YENİ: "Tümü" kategorisi gösterilsin mi
  showSearchBar: boolean; // ✅ YENİ: Arama bölümü gösterilsin mi
  categoryLayout: 'horizontal' | 'vertical'; // ✅ YENİ: Kategori butonları yatay/dikey
  categoryPosition: 'left' | 'right'; // ✅ YENİ: Dikey modda kategori pozisyonu
  categoryFontSize: number; // ✅ YENİ: Kategori yazı boyutu
  isUISettingsModalOpen: boolean;
  
  // Actions
  setIsUISettingsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenUISettingsModal: () => void;
  handleSaveUISettings: (newSettings: UISettings) => void;
}

export const useQuickSaleUI = (): UseQuickSaleUIReturn => {
  const [productCardSize, setProductCardSize] = useState<number>(DEFAULT_CARD_SIZE);
  const [productCardFontSize, setProductCardFontSize] = useState<number>(DEFAULT_FONT_SIZE);
  const [productCategoryTabSize, setProductCategoryTabSize] = useState<number>(DEFAULT_CATEGORY_TAB_SIZE);
  const [productImageSize, setProductImageSize] = useState<number>(DEFAULT_IMAGE_SIZE);
  const [controlPanelPosition, setControlPanelPosition] = useState<'left' | 'right'>(DEFAULT_CONTROL_PANEL_POSITION);
  const [showProductImages, setShowProductImages] = useState<boolean>(DEFAULT_SHOW_PRODUCT_IMAGES); // ✅ YENİ
  const [showProductPrices, setShowProductPrices] = useState<boolean>(DEFAULT_SHOW_PRODUCT_PRICES); // ✅ YENİ
  const [cartColumnWidth, setCartColumnWidth] = useState<number>(DEFAULT_CART_COLUMN_WIDTH); // ✅ YENİ: Sepet sütunu genişliği
  const [showAllCategory, setShowAllCategory] = useState<boolean>(DEFAULT_SHOW_ALL_CATEGORY); // ✅ YENİ: "Tümü" kategorisi
  const [showSearchBar, setShowSearchBar] = useState<boolean>(DEFAULT_SHOW_SEARCH_BAR); // ✅ YENİ: Arama bölümü
  const [categoryLayout, setCategoryLayout] = useState<'horizontal' | 'vertical'>(DEFAULT_CATEGORY_LAYOUT); // ✅ YENİ: Kategori düzeni
  const [categoryPosition, setCategoryPosition] = useState<'left' | 'right'>(DEFAULT_CATEGORY_POSITION); // ✅ YENİ: Dikey modda pozisyon
  const [categoryFontSize, setCategoryFontSize] = useState<number>(DEFAULT_CATEGORY_FONT_SIZE); // ✅ YENİ: Kategori yazı boyutu
  const [isUISettingsModalOpen, setIsUISettingsModalOpen] = useState(false);

  // Component ilk yüklendiğinde localStorage'dan UI ayarlarını yükle
  useEffect(() => {
    try {
      const savedUISettings = localStorage.getItem(QUICK_SALE_UI_SETTINGS_KEY);
      console.log('🎨 [QuickSaleUI] localStorage\'dan yükleniyor:', savedUISettings);
      
      if (savedUISettings) {
        const parsedSettings: UISettings = JSON.parse(savedUISettings);
        console.log('🎨 [QuickSaleUI] Parse edildi:', parsedSettings);
        
        setProductCardSize(parsedSettings.cardSize || DEFAULT_CARD_SIZE);
        setProductCardFontSize(parsedSettings.fontSize || DEFAULT_FONT_SIZE);
        setProductCategoryTabSize(parsedSettings.categoryTabSize || DEFAULT_CATEGORY_TAB_SIZE);
        setProductImageSize(parsedSettings.imageSize || DEFAULT_IMAGE_SIZE);
        setControlPanelPosition(parsedSettings.controlPanelPosition || DEFAULT_CONTROL_PANEL_POSITION);
        setShowProductImages(parsedSettings.showProductImages !== undefined ? parsedSettings.showProductImages : DEFAULT_SHOW_PRODUCT_IMAGES);
        setShowProductPrices(parsedSettings.showProductPrices !== undefined ? parsedSettings.showProductPrices : DEFAULT_SHOW_PRODUCT_PRICES);
        setCartColumnWidth(parsedSettings.cartColumnWidth !== undefined ? parsedSettings.cartColumnWidth : DEFAULT_CART_COLUMN_WIDTH);
        setShowAllCategory(parsedSettings.showAllCategory !== undefined ? parsedSettings.showAllCategory : DEFAULT_SHOW_ALL_CATEGORY);
        // ✅ YENİ: Arama bölümü ve kategori düzeni ayarları
        setShowSearchBar(parsedSettings.showSearchBar !== undefined ? parsedSettings.showSearchBar : DEFAULT_SHOW_SEARCH_BAR);
        setCategoryLayout(parsedSettings.categoryLayout || DEFAULT_CATEGORY_LAYOUT);
        setCategoryPosition(parsedSettings.categoryPosition || DEFAULT_CATEGORY_POSITION);
        setCategoryFontSize(parsedSettings.categoryFontSize || DEFAULT_CATEGORY_FONT_SIZE);
        
        console.log('🎨 [QuickSaleUI] ✅ Ayarlar yüklendi');
      } else {
        console.log('🎨 [QuickSaleUI] ⚠️ localStorage\'da kayıtlı ayar bulunamadı, varsayılanlar kullanılıyor');
      }
    } catch (error) {
      console.error('🎨 [QuickSaleUI] ❌ UI ayarları yüklenirken hata:', error);
    }
  }, []);

  // UI ayarları modalını açma
  const handleOpenUISettingsModal = useCallback(() => {
    setIsUISettingsModalOpen(true);
  }, []);

  // UI ayarlarını kaydetme
  const handleSaveUISettings = useCallback((newSettings: UISettings) => {
    setProductCardSize(newSettings.cardSize);
    setProductCardFontSize(newSettings.fontSize);
    setProductCategoryTabSize(newSettings.categoryTabSize);
    setProductImageSize(newSettings.imageSize);
    setControlPanelPosition(newSettings.controlPanelPosition);
    setShowProductImages(newSettings.showProductImages !== undefined ? newSettings.showProductImages : DEFAULT_SHOW_PRODUCT_IMAGES);
    setShowProductPrices(newSettings.showProductPrices !== undefined ? newSettings.showProductPrices : DEFAULT_SHOW_PRODUCT_PRICES);
    setCartColumnWidth(newSettings.cartColumnWidth !== undefined ? newSettings.cartColumnWidth : DEFAULT_CART_COLUMN_WIDTH);
    setShowAllCategory(newSettings.showAllCategory !== undefined ? newSettings.showAllCategory : DEFAULT_SHOW_ALL_CATEGORY);
    // ✅ YENİ: Arama bölümü ve kategori düzeni ayarları
    setShowSearchBar(newSettings.showSearchBar !== undefined ? newSettings.showSearchBar : DEFAULT_SHOW_SEARCH_BAR);
    setCategoryLayout(newSettings.categoryLayout || DEFAULT_CATEGORY_LAYOUT);
    setCategoryPosition(newSettings.categoryPosition || DEFAULT_CATEGORY_POSITION);
    setCategoryFontSize(newSettings.categoryFontSize || DEFAULT_CATEGORY_FONT_SIZE);

    // localStorage'a kaydet
    try {
      localStorage.setItem(QUICK_SALE_UI_SETTINGS_KEY, JSON.stringify(newSettings));
      console.log('🎨 [QuickSaleUI] ✅ Ayarlar kaydedildi:', newSettings);
    } catch (error) {
      console.error('🎨 [QuickSaleUI] ❌ UI ayarları kaydedilirken hata:', error);
    }

    // Modalı kapat
    setIsUISettingsModalOpen(false);
  }, []);

  return {
    productCardSize,
    productCardFontSize,
    productCategoryTabSize,
    productImageSize,
    controlPanelPosition,
    showProductImages,
    showProductPrices,
    cartColumnWidth,
    showAllCategory,
    showSearchBar, // ✅ YENİ: Arama bölümü gösterilsin mi
    categoryLayout, // ✅ YENİ: Kategori düzeni (yatay/dikey)
    categoryPosition, // ✅ YENİ: Dikey modda pozisyon
    categoryFontSize, // ✅ YENİ: Kategori yazı boyutu
    isUISettingsModalOpen,
    setIsUISettingsModalOpen,
    handleOpenUISettingsModal,
    handleSaveUISettings,
  };
};

